import { Box } from '@mui/material';
import KitModal from '../../../UI/KitModal';
import { getMediaType, getEmbedUrl } from '../utils/mediaUtils';
import { Copy, FileArrowDown, X } from '@phosphor-icons/react';
import IconButtonTooltip from '../../../UI/IconButtonTooltip/IconButtonTooltip';
import { onDownloadFile } from '../../../../util/fileDownloader';
import { useNotificationStore } from '../../../../store/storeNotifications';
import { MediaRenderer } from './MediaRenderers';

interface MediaModalProps {
  show: boolean;
  onHide: () => void;
  url: string;
}

export const MediaModal = ({ show, onHide, url }: MediaModalProps) => {
  const mediaType = getMediaType(url);
  const embedUrl = getEmbedUrl(url, mediaType);
  const setNotification = useNotificationStore(state => state.setNotification);

  const onCopy = () => {
    navigator.clipboard.writeText(url);
  };

  const handleDownload = async () => {
    try {
      let fileType = 'image';
      if (mediaType === 'video') {
        fileType = 'video';
      } else if (mediaType === 'pdf') {
        fileType = 'pdf';
      } else if (mediaType === 'audio') {
        fileType = 'audio';
      }
      await onDownloadFile(url, fileType);
      setNotification({
        type: 'success',
        message: 'File downloaded successfully',
      });
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error.message || 'Error downloading file',
      });
    }
  };

  const maxHeight = window?.innerHeight * 0.9;
  const maxWidth = window?.innerWidth * 0.9;

  return (
    <KitModal
      fullWidth
      showCloseButton={false}
      sx={{
        '& .MuiPaper-root': {
          maxWidth: `${maxWidth}px`,
          width: `${maxWidth}px`,
          maxHeight: `${maxHeight}px`,
          height: `${maxHeight}px`,
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: 0,
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        '& .MuiDialogActions-root': {
          padding: 0,
        },
      }}
      onHide={onHide}
      show={show}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '16px 20px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <IconButtonTooltip
              onClick={handleDownload}
              title="Download"
              sx={{
                p: '8px',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <FileArrowDown color="#344054" size={20} />
            </IconButtonTooltip>

            <IconButtonTooltip
              onClick={onCopy}
              title="Copy URL"
              sx={{
                p: '8px',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Copy color="#344054" size={20} />
            </IconButtonTooltip>

            <IconButtonTooltip
              onClick={onHide}
              title="Close"
              sx={{
                p: '8px',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <X color="#344054" size={20} />
            </IconButtonTooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            flex: 1,
            overflow: 'auto',
            padding: '16px',
            boxSizing: 'border-box',
          }}
        >
          <MediaRenderer
            url={url}
            embedUrl={embedUrl}
            mediaType={mediaType}
            variant="modal"
            maxHeight={maxHeight - 100}
          />
        </Box>
      </Box>
    </KitModal>
  );
};
